import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth.js'
import LanguagePicker from './LanguagePicker.js'

interface NavbarProps {
  theme: 'light' | 'dark'
  onToggleTheme: () => void
}

export default function Navbar({ theme, onToggleTheme }: NavbarProps) {
  const { t }    = useTranslation()
  const { user, signOut } = useAuth()
  const nav      = useNavigate()
  const location = useLocation()
  const isLanding = location.pathname === '/'

  const handleSignOut = async () => {
    await signOut()
    nav('/')
  }

  const scrollTo = (id: string) => {
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-100 flex items-center px-6 gap-4"
      style={{
        height: 58,
        background: theme === 'dark'
          ? 'rgba(8,9,13,0.9)'
          : 'rgba(245,245,243,0.85)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
      }}
    >
      {/* Logo */}
      <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2 no-underline">
        <SilkVoiceLogo />
        <span style={{ fontSize: 17, fontWeight: 400, fontFamily: 'var(--font-serif)', fontStyle: 'italic', color: 'var(--color-text)' }}>
          silkvoice
        </span>
      </Link>

      <div className="flex-1" />

      {user ? (
        <>
          {/* Logged-in nav items are now in the AppShell sidebar.
              Navbar only renders on public pages for logged-in users. */}
          <Link to="/dashboard" className="nav-link hidden md:inline-flex">{t('nav.dashboard')}</Link>

          <LanguagePicker variant="inline" position="below" />

          <button onClick={onToggleTheme} className="theme-toggle" aria-label={t('nav.toggleTheme')}>
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>

          <button
            onClick={handleSignOut}
            className="nav-link"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {t('nav.signOut')}
          </button>
        </>
      ) : (
        <>
          {isLanding ? (
            <>
              <button onClick={() => scrollTo('#how')} className="nav-link hidden md:inline-flex">{t('nav.howItWorks')}</button>
              <button onClick={() => scrollTo('#extension')} className="nav-link hidden md:inline-flex">{t('nav.extension')}</button>
              <button onClick={() => scrollTo('#pricing')} className="nav-link hidden md:inline-flex">{t('nav.pricing')}</button>
              <button onClick={() => scrollTo('#compare')} className="nav-link hidden md:inline-flex">{t('nav.vsCompetitors')}</button>
              <button onClick={() => scrollTo('#langs')} className="nav-link hidden md:inline-flex">{t('nav.languages')}</button>
            </>
          ) : (
            <Link to="/pricing" className="nav-link hidden md:inline-flex">{t('nav.pricing')}</Link>
          )}

          <LanguagePicker variant="inline" position="below" />

          <button onClick={onToggleTheme} className="theme-toggle" aria-label={t('nav.toggleTheme')}>
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>

          <Link to="/auth" className="nav-link hidden md:inline-flex">{t('nav.signIn')}</Link>

          <Link to="/auth" className="btn-nav-cta">
            {t('nav.signUp', 'Sign up')}
          </Link>
        </>
      )}

      <style>{`
        .nav-link {
          color: var(--color-text-muted);
          text-decoration: none;
          font-size: 13px;
          font-weight: 500;
          padding: 5px 11px;
          border-radius: 7px;
          transition: all 0.14s;
          background: none;
          border: none;
          cursor: pointer;
          font-family: inherit;
        }
        .nav-link:hover {
          color: var(--color-text);
          background: rgba(255,255,255,0.07);
        }
        .theme-toggle {
          width: 32px; height: 32px; border-radius: 8px;
          border: 1.5px solid var(--color-glass-border); background: none;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; color: var(--color-muted-ink); transition: all 0.15s;
          flex-shrink: 0;
        }
        .theme-toggle:hover {
          background: rgba(255,255,255,.07); color: var(--color-text);
          border-color: var(--color-faint);
        }
        .theme-toggle svg { width: 15px; height: 15px; stroke: currentColor; fill: none; stroke-width: 1.8; }
        @media (max-width: 860px) {
          .nav-link.hidden { display: none !important; }
        }
      `}</style>
    </nav>
  )
}

/* ── Inline SVG Icons ─────────────────────────────────────────────────────── */

function SilkVoiceLogo() {
  return (
    <svg width="26" height="26" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="nav-g1" x1="300" y1="250" x2="300" y2="750" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FFBE0B" />
          <stop offset="50%" stopColor="#FF006E" />
          <stop offset="100%" stopColor="#00F5D4" />
        </linearGradient>
        <linearGradient id="nav-g2" x1="300" y1="250" x2="800" y2="500" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FFBE0B" />
          <stop offset="100%" stopColor="#3A0CA3" />
        </linearGradient>
        <linearGradient id="nav-g3" x1="300" y1="750" x2="800" y2="500" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#00F5D4" />
          <stop offset="100%" stopColor="#3A0CA3" />
        </linearGradient>
      </defs>
      <path d="M300 250 C150 400,150 600,300 750 C400 650,500 600,500 500 C500 400,400 350,300 250Z" fill="url(#nav-g1)" opacity=".9" />
      <path d="M300 750 C500 850,700 680,800 500 C680 480,550 500,440 500 C440 580,400 650,300 750Z" fill="url(#nav-g3)" />
      <path d="M300 250 C500 150,700 320,800 500 C680 520,550 500,440 500 C440 420,400 350,300 250Z" fill="url(#nav-g2)" />
    </svg>
  )
}

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  )
}

